import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import { ArrowIcon } from "../components/Icons";

// Fallback list shown until /api/variable-categories returns. Real category
// order comes from Settings → Variable Templates → Categories (drag-arranged).
const FALLBACK_CATEGORIES = [
  "Profit & Loss Statement",
  "Cash Flow Statement",
  "Loan Schedule (Secured + Unsecured)",
  "Capital Structure",
  "Unit Economics",
];
const CATEGORY_TAB_LABEL = {
  "Profit & Loss Statement": "P&L Statement",
  "Cash Flow Statement": "Cash Flow",
  "Loan Schedule (Secured + Unsecured)": "Loan Schedule",
  "Capital Structure": "Capital Structure",
  "Unit Economics": "Unit Economics",
};

const fmt = (n, dec = 2) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(dec));
const CR_VARS = new Set([
  "Room Revenue", "F&B Revenue", "OOD Revenue", "Total Revenue",
  "Operating Expenses", "Corporate Expenses",
  "EBIDTA", "PBT", "PAT", "Depreciation", "Finance Cost", "Tax",
]);
const INTEGER_VARS = new Set(["Total Room Count", "Rooms Sold", "No. of Days of Operation"]);

const formatVal = (varName, val) => {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (varName === "Occupancy %") return (n * 100).toFixed(2) + "%";
  if (varName === "ARR") return "₹ " + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  if (CR_VARS.has(varName)) return n.toFixed(2) + " Cr";
  if (INTEGER_VARS.has(varName)) return Math.round(n).toString();
  return fmt(val);
};


/* ─── Companies list page ─── */
export default function CompaniesPage({ onSelectCompany }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    api.getCompanies()
      .then(setCompanies)
      .catch(() => toast("Failed to load companies", "error"))
      .finally(() => setLoading(false));
  }, [toast]);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="card">
        <div className="flex justify-between items-center mb-16">
          <div>
            <h3 className="heading fw-600" style={{ fontSize: 17 }}>Companies</h3>
            <p className="text-xs text-muted mt-4">
              Click a company to view its aggregated P&amp;L, Cash Flow, and other statements summed across every mapped project.
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Code</th>
                <th style={{ textAlign: "right" }}>Projects</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 && (
                <tr><td colSpan={4} className="text-muted" style={{ textAlign: "center", padding: 24 }}>
                  No companies registered yet. Add a company in Settings → Project ↔ Company.
                </td></tr>
              )}
              {companies.map((c) => (
                <tr key={c.id}
                  onClick={() => onSelectCompany?.(c.id, c.name)}
                  style={{ cursor: "pointer" }}>
                  <td className="fw-500">{c.name}</td>
                  <td className="mono text-accent">{c.code || "—"}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{c.project_count ?? 0}</td>
                  <td><ArrowIcon /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ─── Company detail page (aggregated tabs) ─── */
export function CompanyDetail({ companyId, onBack, onNameLoaded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Profit & Loss Statement");
  const [summaryOnly, setSummaryOnly] = useState(false);
  const [variableCategories, setVariableCategories] = useState([]);
  // Hide historical years by default; toggle expands them.
  const [showHistorical, setShowHistorical] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setLoading(true);
    api.getCompanyDashboard(companyId)
      .then((d) => { setData(d); if (onNameLoaded) onNameLoaded(d?.company?.name || ""); })
      .catch(() => toast("Failed to load company dashboard", "error"))
      .finally(() => setLoading(false));
  }, [companyId, toast, onNameLoaded]);

  // Load the user-managed variable categories so the tab strip respects
  // the order defined in Settings → Variable Templates → Categories.
  useEffect(() => {
    api.getVariableCategories()
      .then((rows) => Array.isArray(rows) ? setVariableCategories(rows) : null)
      .catch(() => {});
  }, []);

  const VARIABLE_CATEGORIES = useMemo(() => {
    if (!variableCategories || variableCategories.length === 0) return FALLBACK_CATEGORIES;
    return variableCategories.map((c) => c.name);
  }, [variableCategories]);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data) return <p className="text-muted">Company not found.</p>;

  const { company, projects, variables, years: allYears, actual_year } = data;
  // Filter out historical year columns unless the user opts in.
  const years = showHistorical
    ? allYears
    : (allYears || []).filter((y) => parseInt(y) >= actual_year);
  const historicalCount = (allYears || []).filter((y) => parseInt(y) < actual_year).length;
  const yearLabel = (y) => {
    if (actual_year == null) return "";
    const yi = parseInt(y) - actual_year;
    if (yi < 0) return "Historical";
    if (yi === 0) return "Actual";
    if (yi === 1) return "Budgeted";
    return "Projected";
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="card mb-24">
        <div className="flex justify-between items-start" style={{ flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="flex gap-16 items-center">
              <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
              <span className="badge badge-completed">Company</span>
            </div>
            <h2 className="fw-600 mt-12" style={{ fontSize: 22, color: "var(--text-primary)" }}>{company.name}</h2>
            {company.code && <p className="mono text-accent mt-4">{company.code}</p>}
          </div>
          {historicalCount > 0 && (
            <button className={`btn btn-sm ${showHistorical ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setShowHistorical((p) => !p)}>
              {showHistorical ? "Hide Historical" : `Show Historical (${historicalCount})`}
            </button>
          )}
        </div>
        <div className="flex gap-20 mt-16 items-end" style={{ flexWrap: "wrap" }}>
          <div>
            <span className="text-xxs text-muted" style={{ textTransform: "uppercase" }}>Mapped Projects</span>
            <p className="fw-600 mono text-accent" style={{ fontSize: 14 }}>{projects.length}</p>
          </div>
          <div>
            <span className="text-xxs text-muted" style={{ textTransform: "uppercase" }}>FY Window</span>
            <p className="fw-600" style={{ fontSize: 14 }}>FY {actual_year}–FY {actual_year + 5}</p>
          </div>
        </div>
        {projects.length > 0 && (
          <div className="flex gap-6 mt-12" style={{ flexWrap: "wrap" }}>
            {projects.map((p) => (
              <span key={p.id} className="text-xxs"
                style={{ background: "rgba(255,255,255,0.05)", padding: "3px 10px", borderRadius: 20 }}>
                {p.name} <span className="text-dim" style={{ marginLeft: 4 }}>({p.status})</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs mb-20">
        {VARIABLE_CATEGORIES.map((c) => (
          <button key={c} className={`tab ${activeTab === c ? "active" : ""}`}
            onClick={() => setActiveTab(c)}>{CATEGORY_TAB_LABEL[c] || c}</button>
        ))}
      </div>

      {/* Aggregated table per category */}
      {(() => {
        const inCategory = variables.filter((v) => (v.category || "") === activeTab);
        const filtered = summaryOnly ? inCategory.filter((v) => v.subtotal) : inCategory;
        const subtotalCount = inCategory.filter((v) => v.subtotal).length;
        return (
          <div className="card">
            <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
              <div>
                <h3 className="heading fw-600" style={{ fontSize: 16 }}>{activeTab}</h3>
                <p className="text-xs text-muted mt-4">
                  Auto-aggregated (summed) across {projects.length} project{projects.length === 1 ? "" : "s"} mapped to this company.
                </p>
              </div>
              <div className="tabs" style={{ padding: 3 }}>
                <button type="button" className={`tab ${!summaryOnly ? "active" : ""}`}
                  onClick={() => setSummaryOnly(false)}>
                  All <span className="text-xxs text-muted" style={{ marginLeft: 6 }}>({inCategory.length})</span>
                </button>
                <button type="button" className={`tab ${summaryOnly ? "active" : ""}`}
                  disabled={subtotalCount === 0}
                  onClick={() => setSummaryOnly(true)}>
                  Summary only <span className="text-xxs text-muted" style={{ marginLeft: 6 }}>({subtotalCount})</span>
                </button>
              </div>
            </div>

            {filtered.length === 0 && (
              <p className="text-muted" style={{ padding: 20 }}>
                {inCategory.length === 0
                  ? <>No variables under <strong>{activeTab}</strong> across this company's projects.</>
                  : <>No subtotal/summary rows. Switch to "All" to see every variable.</>}
              </p>
            )}

            {filtered.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th style={{ minWidth: 280, width: "32%" }}>Particulars</th>
                    {years.map((y) => (
                      <th key={y} style={{ textAlign: "right", width: 96 }}>
                        <div className="text-xxs text-dim">{yearLabel(y)}</div>FY {y}
                      </th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filtered.map((v) => {
                      const isSub = !!v.subtotal;
                      const rowStyle = isSub
                        ? {
                            background: "rgba(124,143,245,0.14)",
                            fontWeight: 700,
                            borderTop: "2px solid rgba(124,143,245,0.40)",
                          }
                        : undefined;
                      return (
                        <tr key={v.name} style={rowStyle}>
                          <td className={isSub ? "fw-700" : "fw-500"}
                            style={isSub ? { color: "var(--accent)" } : undefined}>
                            <div className="flex items-center gap-6" style={{ flexWrap: "wrap" }}>
                              <span>{v.name}</span>
                              {isSub && (
                                <span className="text-xxs fw-600" style={{
                                  padding: "2px 8px", borderRadius: 12,
                                  background: "rgba(124,143,245,0.20)", color: "var(--accent)",
                                  border: "1px solid rgba(124,143,245,0.45)",
                                  letterSpacing: 0.4, textTransform: "uppercase",
                                }}>Total</span>
                              )}
                            </div>
                            {v.contributing && v.contributing.length < projects.length && (
                              <p className="text-xxs text-dim mt-2">
                                Sourced from {v.contributing.length} of {projects.length} projects
                              </p>
                            )}
                          </td>
                          {years.map((y) => (
                            <td key={y} className="mono text-xs"
                              style={{ textAlign: "right", fontWeight: isSub ? 700 : undefined }}>
                              {formatVal(v.name, v.values?.[y])}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
